import {push} from 'connected-react-router';
import jwt from 'jsonwebtoken';
import * as api from '../api';
import {ROLES} from '../constants';
import {showMessage} from './messages';
import {getPartners, getBookings, getFavouritePartners, getSubscriptions, getEvents, getFavouriteRtvChannels, postUsageData, playerGetRtvCategories, getPlayerAffiliations} from './player';
import {getOwnedFields, getOwnedServices, getOwnedRtvChannels, partnerFieldsActiveTab, partnerGetStoragePrices, getPartnerPermanentVideos, getPartnerPlaylists, getPartnerVideos, partnerGetRtvCategories, partnerGetAffiliations} from './partner';
import Cookies from 'universal-cookie';
import i18n from '../locale/i18n';
import {getSponsors, getStoragePrices, searchPartners, getRtvCategories} from './admin';

const getLoginNextPath = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const nextPath = new URLSearchParams(window.location.search || '').get('next');
  if (!nextPath || !nextPath.startsWith('/') || nextPath.startsWith('//')) {
    return null;
  }

  return nextPath;
};

// Auth
export const loginSuccessfull = (token, profile) => ({
  type: 'LOGIN_SUCCESSFULL',
  token,
  profile
});

export const checkToken = ({token, email}, noRedirect = false, publicRoute = false) => async dispatch => {
  const tokenData = jwt.decode(token);
  let profile = null;
  if (tokenData.role === ROLES.player) {
    profile = await api.getPlayerProfile({uid: tokenData.id, email, role: tokenData.role}, token);
  } else if (tokenData.role === ROLES.partner) {
    profile = await api.getPartnerProfile({uid: tokenData.id, email, role: tokenData.role}, token);
  }

  if ((tokenData.role === ROLES.player || tokenData.role === ROLES.partner) && profile.status === 200) {
    dispatch(loginSuccessfull(token, profile.json));
    if (publicRoute) {
      if (tokenData.role === ROLES.player) {
        dispatch(getSubscriptions());
      }
      return;
    }
    if (tokenData.role === ROLES.player) {
      dispatch(getPartners());
      dispatch(getFavouritePartners());
      dispatch(getFavouriteRtvChannels());
      dispatch(getBookings('all'));
      dispatch(getSubscriptions());
      dispatch(getEvents());
      dispatch(playerGetRtvCategories());
      dispatch(getPlayerAffiliations());
      dispatch(postUsageData(0));
      if (!noRedirect) {
        dispatch(push('/'));
      }
    }

    if (tokenData.role === ROLES.partner) {
      dispatch(getOwnedFields());
      dispatch(getOwnedServices());
      dispatch(getOwnedRtvChannels());
      dispatch(partnerGetStoragePrices());
      dispatch(getPartnerPermanentVideos());
      dispatch(getPartnerVideos());
      dispatch(getPartnerPlaylists());
      dispatch(postUsageData(0));
      dispatch(partnerGetRtvCategories());
      dispatch(partnerGetAffiliations());
      if (profile.json.isSpecialist) dispatch(partnerFieldsActiveTab("servizi"));
      if (!noRedirect) {
        dispatch(push('/partner-fields'));
      }
    }
  } else if (tokenData.role === ROLES.admin) {
    dispatch(loginSuccessfull(token, {user: {role: ROLES.admin}}));
    if (publicRoute) {
      return;
    }
    dispatch(getSponsors());
    dispatch(getStoragePrices());
    dispatch(postUsageData(0));
    dispatch(searchPartners('all'));
    dispatch(getRtvCategories());
    if (!noRedirect) {
      dispatch(push('/'));
    }
  } else {
    if (publicRoute) {
      return;
    }
    dispatch(showMessage('SESSION', 'INVALID_SESSION'));
    dispatch(push('/login'));
  }
};

export const login = credential => async (dispatch, getState) => {
  const res = await api.login(credential);
  if (res.status === 200) {
    const cookies = new Cookies();
    cookies.set('replayerAuth', {token: res.json.token, email: credential.email}, {
      path: '/',
      sameSite: 'lax'
    });
    const tokenData = jwt.decode(res.json.token);
    const nextPath = getLoginNextPath();
    let profile = null;
    if (tokenData.role === ROLES.player) {
      profile = await api.getPlayerProfile({uid: tokenData.id, email: credential.email, role: tokenData.role}, res.json.token);
      dispatch(updateLanguage(i18n.language, res.json.token));
    } else if (tokenData.role === ROLES.partner) {
      profile = await api.getPartnerProfile({uid: tokenData.id, email: credential.email, role: tokenData.role}, res.json.token);
      dispatch(updateLanguage(i18n.language, res.json.token));
    }

    if (tokenData.role === ROLES.player) {
      dispatch(loginSuccessfull(res.json.token, profile.json));
      dispatch(getPartners());
      dispatch(getFavouritePartners());
      dispatch(getFavouriteRtvChannels());
      dispatch(getBookings('all'));
      dispatch(getSubscriptions());
      dispatch(getEvents());
      dispatch(getPlayerAffiliations());
      dispatch(postUsageData(0));
      dispatch(playerGetRtvCategories());
      dispatch(push(nextPath || '/'));
    }

    if (tokenData.role === ROLES.partner) {
      const partnerNextPath = nextPath && !nextPath.startsWith('/club/')
        ? nextPath
        : '/partner-fields';
      dispatch(loginSuccessfull(res.json.token, profile.json));
      dispatch(getOwnedFields());
      dispatch(getOwnedServices());
      dispatch(getOwnedRtvChannels());
      dispatch(partnerGetStoragePrices());
      dispatch(getPartnerPermanentVideos());
      dispatch(getPartnerVideos());
      dispatch(getPartnerPlaylists());
      dispatch(postUsageData(0));
      dispatch(partnerGetRtvCategories());
      dispatch(partnerGetAffiliations());
      if (profile.json.isSpecialist) dispatch(partnerFieldsActiveTab("servizi"));
      dispatch(push(partnerNextPath));
    }

    if (tokenData.role === ROLES.admin) {
      const adminNextPath = nextPath && !nextPath.startsWith('/club/')
        ? nextPath
        : '/';
      dispatch(loginSuccessfull(res.json.token, {user: {role: ROLES.admin}}));
      dispatch(getStoragePrices());
      dispatch(postUsageData(0));
      dispatch(searchPartners('all'));
      dispatch(getRtvCategories());
      dispatch(push(adminNextPath));
    }
  } else if (res.status === 403 && res.json.error === 'invalid_credentials') {
    dispatch(showMessage('LOGIN', 'LOGIN_FAILED'));
  } else if (res.status === 403 && res.json.error === 'user_not_actived') {
    dispatch(showMessage('LOGIN', 'LOGIN_USER_NOT_ACTIVE'));
  } else {
    dispatch(showMessage('LOGIN', `Error ${res.status}: ${res.json.error}`));
  }
};

const completeExternalLogin = async (dispatch, result) => {
  const cookies = new Cookies();
  cookies.set('replayerAuth', {token: result.token, email: result.email}, {
    path: '/',
    sameSite: 'lax'
  });
  await dispatch(checkToken({token: result.token, email: result.email}, true));
  dispatch(updateLanguage(i18n.language, result.token));
  dispatch(push(getLoginNextPath() || '/'));
};

export const loginGoogle = credential => async dispatch => {
  const res = await api.googleLogin(credential);
  if (res.status === 200) {
    await completeExternalLogin(dispatch, res.json);
  } else if (res.status !== 202) {
    dispatch(showMessage('LOGIN', `Google: ${res.json.error || res.status}`));
  }
  return res;
};

export const registerGoogle = data => async dispatch => {
  const res = await api.googleRegister({...data, language: i18n.language});
  if (res.status === 200 || res.status === 201) {
    await completeExternalLogin(dispatch, res.json);
  } else {
    dispatch(showMessage('SIGNING_UP', `Google: ${res.json.error || res.status}`));
  }
  return res;
};

export const loginApple = data => async dispatch => {
  const res = await api.appleLogin(data);
  if (res.status === 200) {
    await completeExternalLogin(dispatch, res.json);
  } else if (res.status !== 202) {
    dispatch(showMessage('LOGIN', `Apple: ${res.json.error || res.status}`));
  }
  return res;
};

export const registerApple = data => async dispatch => {
  const res = await api.appleRegister({...data, language: i18n.language});
  if (res.status === 200 || res.status === 201) {
    await completeExternalLogin(dispatch, res.json);
  } else {
    dispatch(showMessage('SIGNING_UP', `Apple: ${res.json.error || res.status}`));
  }
  return res;
};

export const linkExistingApple = data => async dispatch => {
  const res = await api.appleLinkExisting(data);
  if (res.status === 200) {
    await completeExternalLogin(dispatch, res.json);
  } else if (res.status === 403 && res.json.error === 'invalid_credentials') {
    dispatch(showMessage('LOGIN', 'LOGIN_FAILED'));
  } else {
    dispatch(showMessage('LOGIN', `Apple: ${res.json.error || res.status}`));
  }
  return res;
};

export const signUpPlayer = signUpData => async dispatch => {
  const nextPath = getLoginNextPath();
  signUpData.language = i18n.language;
  const res = await api.signUpPlayer(signUpData);
  if (res.status === 200 || res.status === 201) {
    dispatch(push(nextPath ? `/login?next=${encodeURIComponent(nextPath)}` : '/'));
    dispatch(showMessage('SIGNING_UP', 'SIGNING_UP_SUCCESSFUL'));
  } else if (res.status === 400 && res.json.error === 'email_already_registered') {
    dispatch(showMessage('SIGNING_UP', 'EMAIL_ALREADY_REGISTERED'));
  } else if (res.status === 400 && res.json.error === 'password_not_matched') {
    dispatch(showMessage('SIGNING_UP', 'PASSWORD_NO_MATCHED'));
  } else if (res.status === 400 && res.json.error === 'invalid_email') {
    dispatch(showMessage('SIGNING_UP', 'EMAIL_BAD_FORMAT'));
  } else {
    dispatch(showMessage('SIGNING_UP', `Error ${res.status}: ${res.json.error}`));
  }
};

export const signUpPartner = signUpData => async dispatch => {
  const res = await api.signUpPartner(signUpData);
  if (res.status === 200 || res.status === 201) {
    dispatch(push('/'));
    dispatch(showMessage('SIGNING_UP_REQUEST', 'SIGNING_UP_REQUEST_SUCCESSFUL'));
  } else {
    dispatch(showMessage('SIGNING_UP_REQUEST', `Error ${res.status}: ${res.json.error}`));
  }
};

export const logout = () => async dispatch => {
  const cookies = new Cookies();
  cookies.remove('replayerAuth', {path: '/'});
  dispatch({
    type: 'LOGOUT'
  });
};

export const showRecoveryPasswordModal = () => ({
  type: 'LOGIN_SHOW_RECOVERY_PASSWORD_MODAL'
});

export const hideRecoveryPasswordModal = () => ({
  type: 'LOGIN_HIDE_RECOVERY_PASSWORD_MODAL'
});

export const recoveryPassword = email => async dispatch => {
  const res = await api.sendResetPwd(email);
  if (res.status === 200 || res.status === 201) {
    dispatch(push('/'));
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', 'PASSWORD_RECOVERY_REQUEST_SUCCESSFUL'));
  } else {
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', `Error ${res.status}: ${res.json.error}`));
  }
};

export const verifyEmail = token => async dispatch => {
  const res = await api.verifyEmail(token);
  if (res.status === 200 || res.status === 201) {
    dispatch(showMessage('WELCOME', 'LOGIN_REQUIRED'));
  } else {
    dispatch(showMessage('VERIFY_EMAIL', 'VERIFY_EMAIL_FAILED'));
  }

  dispatch(push('/login'));
};

export const resetPassword = (data, token) => async dispatch => {
  const res = await api.resetPassword(data, token);
  if (res.status === 200 || res.status === 201) {
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', 'CHANGE_PASSWORD_SUCCESSFUL'));
  } else if (res.status === 400 && res.json.error === 'password_not_matched') {
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', 'PASSWORD_NO_MATCHED'));
  } else if (res.status === 403 && res.json.error === 'invalid_token') {
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', 'CHANGE_PASSWORD_REQUEST_EXPIRED'));
  } else {
    dispatch(showMessage('LOGIN_PASSWORD_RECOVERY', `Error ${res.status}: ${res.json.error}`));
  }

  dispatch(push('/login'));
};

export const confirmChangeEmail = token => async dispatch => {
  const res = await api.confirmChangeEmail(token);
  if (res.status === 200 || res.status === 201) {
    dispatch(showMessage('CONFIRM_EMAIL', 'CONFIRM_EMAIL_SUCCESSFUL'));
  } else {
    dispatch(showMessage('CONFIRM_EMAIL', 'CONFIRM_EMAIL_FAILED'));
  }

  dispatch(push('/login'));
};

export const updateLanguage = (language, token) => async (dispatch, getState) => {
  const res = await api.updateUserLanguage(language, token);

  if (res.status !== 200) {
    dispatch(showMessage('CHANGE_LANGUAGE', `Error ${res.status}: ${res.json.error}`));
  }
};
